import {
  createContext, useContext, useRef, useState, useCallback, useEffect, ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import type { LocalTrack, RemoteTrack } from 'livekit-client';
import {
  actOnCall,
  actOnCallKeepalive,
  createCallEventId,
  getCallSignaling,
  getCallSnapshot,
  getIceServers,
  getLiveKitCredentials,
  getP2pOffer,
  logCall,
  logCallEvent,
  logCallKeepalive,
  postCallSignals,
  reportCallAlerted,
  startCall as startAuthoritativeCall,
  syncCalls,
  CallLogCreate,
  CallOutcome,
} from '../api/calls';
import type {
  CallEventEnvelope,
  CallLifecycleAction,
  CallSnapshot,
} from '../types/calls';
import { calleeReportedRinging, isDirectSfuSnapshot } from '../types/calls';
import { setCallActive, isOtherCallActive } from '../utils/call/callBusy';
import { describeCallError, logCallDiagnostic } from '../utils/call/callDiagnostics';
import { deriveOutcome } from '../utils/call/callLog';
import { SpeakingMonitor } from '../utils/call/speaking';
import { cameraErrorMessage } from '../utils/call/cameraErrors';
import { bumpMissed } from '../utils/call/missedBadge';
import { bindDirectCallRelease, noteOffDirectCall, noteOnDirectCall } from '../utils/call/continuedCalls';
import { getConversation } from '../api/chat';
import { clearNativeIncomingCall, getPendingNativeCall } from '../utils/mobileIncomingCall';
import { PeerService } from '../webrtc/PeerService';
import { DirectSfuMedia } from '../webrtc/DirectSfuMedia';
import { canShareScreen, screenShareErrorMessage } from '../utils/call/screenShare';
import { startRecordingForCall, useCallRecording } from '../hooks/call/useCallRecording';
import { useAuth } from './AuthContext';
import CallScreen from '../components/call/CallScreen';
import StartCallPicker from '../components/calls/StartCallPicker';
import { useDndQuietMode } from './DndQuietModeContext';
import {
  startRingtone, startRingback, stopAllCallSounds, playConnectTone, playEndTone,
  setSystemRingtone,
} from '../utils/call/callSounds';
import {
  getSystemRingtone, showCallNotification, closeCallNotification,
  onCallNotificationAction, showDesktopNotification,
} from '../utils/electron';
import {
  selectProtectIpAddress, selectSelectedAudioOutputId, selectAudioMutedByDefault,
} from '../redux/slices/chatPreferences';

export type CallStatus =
  | 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'connected' | 'ended';

export interface CallState {
  status: CallStatus;
  callId: string | null;
  conversationId: string | null;
  peerUserId: string | null;
  peerName: string | null;
  peerAvatarUrl: string | null;
  muted: boolean;
  startedAt: number | null;
  endReason: string | null;
  /** Outgoing only: the callee's device reported it is showing the call. */
  peerRinging: boolean;
}

const IDLE: CallState = {
  status: 'idle', callId: null, conversationId: null, peerUserId: null,
  peerName: null, peerAvatarUrl: null, muted: false, startedAt: null, endReason: null, peerRinging: false,
};

/** Auto-end a call that rings unanswered this long (typical no-answer timeout). */
// A high-priority push can still take several seconds to wake a sleeping
// mobile recipient. Leave enough time for the recipient to actually answer.
const RING_TIMEOUT_MS = 90_000;

/**
 * Media must connect this soon after a call goes active. Before the backend
 * relayed answers after a v2 accept, callers sat in "connecting" forever.
 */
export const CONNECT_WATCHDOG_MS = 30_000;

/**
 * Grace period before tearing down a call that bounced to 'disconnected'.
 * WebRTC commonly flaps a healthy connection to 'disconnected' on a brief
 * network blip / Wi-Fi handoff and then recovers to 'connected' on its own.
 * Only tear down if it has NOT recovered (or moved to 'failed') by the time
 * this elapses — so a momentary hiccup can't kill an otherwise-fine call.
 */
const DISCONNECT_GRACE_MS = 6_000;

/**
 * While media is connecting, pull the answer and ICE candidates the server
 * kept for this side. Their socket relay is best effort: a phone answering
 * from the lock screen swaps its socket right after answering, and the
 * answer in flight was lost, leaving this caller on "Connecting..." until the
 * watchdog failed the call.
 */
export const SIGNALING_RECOVERY_FIRST_MS = 2_000;
const SIGNALING_RECOVERY_EVERY_MS = 3_000;
/** A peer gathers a few candidates per interface; this only bounds a bad one. */
const MAX_BUFFERED_CANDIDATES = 100;

function candidateKey(candidate: RTCIceCandidateInit | null | undefined): string | null {
  const text = candidate?.candidate?.trim();
  if (!text) return null;
  return `${candidate?.sdpMid ?? ''}|${candidate?.sdpMLineIndex ?? ''}|${text}`;
}

/**
 * One greppable main.log line per call milestone (`grep CALL_`): ids, roles
 * and states only, never names, SDP, candidates or tokens.
 */
function logCallLifecycle(
  event: string,
  callId: string | null | undefined,
  fields: Record<string, string | number | boolean | null | undefined> = {},
): void {
  const parts = Object.entries(fields)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${key}=${value}`);
  logCallDiagnostic(
    `${event} call=${callId ?? '-'}${parts.length ? ` ${parts.join(' ')}` : ''} platform=desktop at=${new Date().toISOString()}`,
  );
}

/**
 * The action that ends this call for this participant. The server lets only
 * a direct call's creator cancel or end it and refuses anyone else's `end`
 * with 403, so the callee leaves instead. Its `end` used to be refused: the
 * call came down on the callee's screen only, and the caller sat on
 * "Connecting..." until its media timed out.
 */
function terminalActionFor(isCaller: boolean, stillRinging: boolean, directSfu = false): CallLifecycleAction {
  // A direct SFU call is left by either side once answered; the server ends
  // it for both (call-recording contract).
  if (directSfu && !stillRinging) return 'leave';
  if (isCaller) return stillRinging ? 'cancel' : 'end';
  return stillRinging ? 'decline' : 'leave';
}

/** How long the callee waits for the caller's name before ringing anyway. */
const DIRECT_PEER_LOOKUP_MS = 1_500;

function isRinging(status: CallStatus): boolean {
  return status === 'outgoing' || status === 'incoming';
}

function isTerminalSnapshot(status: CallSnapshot['status']): boolean {
  return status === 'ended' || status === 'declined' || status === 'cancelled' || status === 'missed';
}

function isAuthorizedRingingInvite(frame: any, authorized: CallSnapshot): boolean {
  return authorized.callId === frame.call_id
    && authorized.conversationId === frame.conversation_id
    && authorized.initiatorUserId === frame.from_user_id
    && authorized.participantUserIds.includes(frame.from_user_id)
    && authorized.mediaTopology === 'p2p'
    && !authorized.isGroup
    && authorized.status === 'ringing';
}

type SendFn = (frame: Record<string, any>) => void;

interface CallApi {
  call: CallState;
  snapshot: CallSnapshot | null;
  /** Whether the active call is collapsed to the floating pill. */
  minimized: boolean;
  startCall: (conversationId: string, peerUserId: string, peerName?: string | null, peerAvatarUrl?: string | null) => Promise<void>;
  accept: () => Promise<void>;
  reject: () => void;
  hangup: () => void;
  toggleMute: () => void;
  /**
   * Turn this side's camera on or off. Direct SFU calls only: a P2P 1:1 is
   * audio-only, and there this does nothing.
   */
  toggleVideo: () => void;
  toggleMinimize: () => void;
  minimize: () => void;
  /** Called by the WS layer to deliver an inbound call-* frame. */
  handleSignal: (frame: any) => void;
  /** Called by the WS layer to deliver an authoritative lifecycle frame. */
  handleCallState: (frame: CallEventEnvelope) => void;
  /** Reconcile persisted calls after initial load or WebSocket reconnect. */
  recoverCalls: () => Promise<void>;
  /** Called by the WS layer to register the outbound send function. */
  bindSend: (fn: SendFn) => void;
  /** Register the 1:1→group escalation handler (provided by CallEscalationBridge). */
  bindEscalate: (fn: (args: { callId: string | null; peerUserId: string; peerName: string | null; newUserIds: string[] }) => void) => void;
}

const Ctx = createContext<CallApi | null>(null);

function useOptionalPreference<T>(selector: (state: any) => T, fallback: T): T {
  // Some embedded call consumers (and the focused provider tests) mount the
  // provider outside the app Redux shell. Call preferences are additive, so
  // those surfaces retain safe defaults instead of making calling unusable.
  try { return useSelector(selector); } catch { return fallback; }
}

function useOptionalSelfId(): string {
  // Same reasoning as above: focused provider tests mount without auth. With
  // no identity, an incoming direct SFU ring cannot be told apart from this
  // user's own outgoing call, so it is never opened.
  try { return useAuth().user?.id ?? ''; } catch { return ''; }
}

export function CallProvider({ children }: { children: ReactNode }) {
  const { isDndQuiet } = useDndQuietMode();
  const protectIpAddress = useOptionalPreference(selectProtectIpAddress, false);
  const selectedAudioOutputId = useOptionalPreference(selectSelectedAudioOutputId, null);
  const audioMutedByDefault = useOptionalPreference(selectAudioMutedByDefault, false);
  const selfId = useOptionalSelfId();
  const [call, setCall] = useState<CallState>(IDLE);
  const [snapshot, setSnapshot] = useState<CallSnapshot | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [addingOpen, setAddingOpen] = useState(false);
  const [remoteSpeaking, setRemoteSpeaking] = useState(false);
  // Camera video, direct SFU calls only. `videoReady` is true once the room
  // is up (that is when the camera can be published); the tracks are what
  // the room reports, so "camera on" is never a guess.
  const [videoReady, setVideoReady] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [localVideoTrack, setLocalVideoTrack] = useState<LocalTrack | null>(null);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState<RemoteTrack | null>(null);
  const [remoteScreenTrack, setRemoteScreenTrack] = useState<RemoteTrack | null>(null);
  const [screenSharing, setScreenSharing] = useState(false);
  const [screenShareBusy, setScreenShareBusy] = useState(false);
  const escalateRef = useRef<((args: { callId: string | null; peerUserId: string; peerName: string | null; newUserIds: string[] }) => void) | null>(null);
  const peerRef = useRef<PeerService | null>(null);
  // Direct SFU (a 1:1 routed through LiveKit so it can be recorded): the
  // media transport instead of peerRef. The lifecycle, UI and logging are
  // shared with P2P; only the transport and the hang-up action differ.
  const sfuMediaRef = useRef<DirectSfuMedia | null>(null);
  const directSfuRef = useRef(false);
  const openingDirectRef = useRef<string | null>(null);
  const openDirectIncomingRef = useRef<(snapshot: CallSnapshot) => Promise<void>>(async () => {});
  const snapshotRef = useRef<CallSnapshot | null>(null);
  const startingCallRef = useRef(false);
  const pendingActionRef = useRef<{ callId: string; eventId: string } | null>(null);
  const recoveryInFlightRef = useRef<Promise<void> | null>(null);
  const unloadActionRef = useRef<{ callId: string; eventId: string } | null>(null);
  const sendRef = useRef<SendFn>(() => {});
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  // Detects when the peer is talking → drives the avatar speaking wave.
  const speakingRef = useRef<SpeakingMonitor | null>(null);
  // Pending grace timer for a transient 'disconnected'. Armed when the peer
  // connection flaps to 'disconnected', and cleared the moment it recovers to
  // 'connected', moves to 'failed'/'closed', or the call tears down.
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Buffer for ICE candidates that arrive before the PeerService exists
  // (callee receives candidates before pressing Accept).
  const pendingCandidates = useRef<RTCIceCandidateInit[]>([]);
  const pendingOffer = useRef<RTCSessionDescriptionInit | null>(null);
  // Mirror of the live call routing info for use inside callbacks.
  const routeRef = useRef<{ callId: string; conversationId: string; peerUserId: string } | null>(null);
  // Outgoing: a snapshot of this call showed the callee "invited" (see calleeReportedRinging).
  const peerInvitedRef = useRef(false);
  // Only the caller applies a remote answer, and only once — guards against a
  // self-call (same account in two windows) or a duplicate frame, which would
  // otherwise throw "setRemoteDescription ... wrong state: stable".
  const isCallerRef = useRef(false);
  const answerAppliedRef = useRef(false);
  // This side's own gathered ICE candidates. Candidates relayed while the
  // callee's socket was still offline (killed mobile app answering from a
  // push) are dropped, so the caller replays them once the answer proves the
  // callee is online, and either side re-posts them over HTTPS while media is
  // still connecting.
  const localCandidatesRef = useRef<any[]>([]);
  const postedCandidateKeysRef = useRef<Set<string>>(new Set());
  // Remote candidates are only valid once the remote description is applied;
  // before that they are buffered. Keys dedupe the socket and recovery copies.
  const remoteDescriptionAppliedRef = useRef(false);
  const seenCandidateKeysRef = useRef<Set<string>>(new Set());
  const signalingRecoveryInFlightRef = useRef(false);
  // The callee's answer, kept until the server has acknowledged it. Its own
  // candidates wait for the answer to be sent: a caller can only apply them
  // after it, and older desktop builds dropped any that arrived first.
  const localAnswerRef = useRef<RTCSessionDescriptionInit | null>(null);
  const answerPostedRef = useRef(false);
  const answerSentRef = useRef(false);
  // Always-current mirror of call.status for use in unmount/unload handlers
  // whose effect runs only once (stable deps) and would otherwise see a stale
  // status from the closure.
  const statusRef = useRef(call.status);
  statusRef.current = call.status;
  // Cross-tab coordination: the backend fans a call-invite out to EVERY socket
  // the user has open, so each tab rings independently with its own audio state.
  // A reject only goes to the caller, never the user's other tabs — so without
  // this they'd ring on until their own 45s timeout. We broadcast a resolution
  // on this channel so sibling tabs dismiss the same call at once.
  const callChannelRef = useRef<BroadcastChannel | null>(null);
  // call_ids we've already resolved (declined/ended), so a duplicate or
  // re-published call-invite for the same id can't re-ring. Bounded FIFO.
  const declinedCallIdsRef = useRef<string[]>([]);
  // Snapshot of the live call's identity/timing, captured at start/invite and
  // updated on connect, so logCallOutcome() can build a record even as cleanup
  // tears the rest down. Direction is from this side's perspective.
  const callMetaRef = useRef<{
    callId: string; peerUserId: string; conversationId: string;
    direction: 'incoming' | 'outgoing'; ringStartedAt: number; answeredAt: number | null;
  } | null>(null);
  // call_ids already logged by THIS client, so cleanup + the unload beacon
  // can't double-post the same side. Bounded FIFO.
  const loggedCallIdsRef = useRef<string[]>([]);
  const eventLoggedRef = useRef<string[]>([]);
  const nativeCallIdRef = useRef<string | null>(null);

  const bindSend = useCallback((fn: SendFn) => { sendRef.current = fn; }, []);

  const bindEscalate = useCallback((fn: (args: { callId: string | null; peerUserId: string; peerName: string | null; newUserIds: string[] }) => void) => {
    escalateRef.current = fn;
  }, []);

  const sendFrame = useCallback((type: string, extra: Record<string, any> = {}) => {
    const r = routeRef.current;
    if (!r) return;
    sendRef.current({
      type, call_id: r.callId, conversation_id: r.conversationId, to_user_id: r.peerUserId, ...extra,
    });
  }, []);

  const logCallOutcome = useCallback((outcome: CallOutcome) => {
    const meta = callMetaRef.current;
    if (!meta) return;
    if (loggedCallIdsRef.current.includes(meta.callId)) return;
    loggedCallIdsRef.current.push(meta.callId);
    if (loggedCallIdsRef.current.length > 50) loggedCallIdsRef.current.shift();
    const endedAt = Date.now();
    const payload: CallLogCreate = {
      call_id: meta.callId,
      peer_user_id: meta.peerUserId,
      conversation_id: meta.conversationId,
      direction: meta.direction,
      call_type: 'voice',
      outcome,
      started_at: new Date(meta.ringStartedAt).toISOString(),
      answered_at: meta.answeredAt ? new Date(meta.answeredAt).toISOString() : null,
      ended_at: new Date(endedAt).toISOString(),
      duration_s: meta.answeredAt ? Math.max(0, Math.round((endedAt - meta.answeredAt) / 1000)) : 0,
    };
    void logCall(payload).catch(() => { /* best-effort; peer's row is independent */ });
    if (meta.direction === 'incoming' && outcome === 'missed') bumpMissed();
  }, []);

  const cleanup = useCallback((
    reason: string | null,
    broadcast = true,
    outcome: CallOutcome | 'none' | 'derive' = 'derive',
  ) => {
    // Stop the ring SYNCHRONOUSLY here — cleanup() is the single choke point
    // every teardown (reject/hangup/cancel/end/timeout/connection-lost) passes
    // through. Relying on the status-driven useEffect proved unreliable: on a
    // decline the status went incoming→idle and the overlay closed, but the
    // effect did not always run stopAllCallSounds, so the loop kept ringing
    // until the 45s timeout. A direct call here can never be skipped.
    stopAllCallSounds();
    if (routeRef.current) {
      logCallDiagnostic(
        `closed call=${routeRef.current.callId} ui=${statusRef.current} reason=${reason ?? '-'} outcome=${outcome}`,
      );
      logCallLifecycle('CALL_ENDED', routeRef.current.callId, {
        peer: routeRef.current.peerUserId,
        role: isCallerRef.current ? 'caller' : 'callee',
        ui: statusRef.current,
        reason: reason ? JSON.stringify(reason) : null,
        outcome,
      });
    }
    setCallActive('oneToOne', false);
    // Log this side's perspective exactly once, before the meta is cleared.
    const meta = callMetaRef.current;
    const resolved: CallOutcome | 'none' =
      outcome === 'derive' ? deriveOutcome(reason, meta?.answeredAt != null) : outcome;
    if (resolved !== 'none') logCallOutcome(resolved);
    if (meta) noteOffDirectCall(meta.callId);
    // Shared in-thread call_event — caller side only (owns the call_id origin).
    // Idempotent server-side on call_id, so a retry/echo is harmless.
    if (meta && isCallerRef.current && resolved !== 'none' && !eventLoggedRef.current.includes(meta.callId)) {
      eventLoggedRef.current.push(meta.callId);
      const endedAt = Date.now();
      void logCallEvent({
        call_id: meta.callId,
        conversation_id: meta.conversationId,
        call_type: 'voice',
        outcome: resolved,
        is_group: false,
        duration_s: meta.answeredAt ? Math.max(0, Math.round((endedAt - meta.answeredAt) / 1000)) : 0,
        started_at: new Date(meta.ringStartedAt).toISOString(),
        ended_at: new Date(endedAt).toISOString(),
        peer_user_id: meta.peerUserId,
      }).catch(() => {});
    }
    const resolvedId = routeRef.current?.callId;
    if (resolvedId) {
      // Remember it so a duplicate/re-published invite for this id can't re-ring.
      const seen = declinedCallIdsRef.current;
      if (!seen.includes(resolvedId)) {
        seen.push(resolvedId);
        if (seen.length > 50) seen.shift();
      }
      // Tell this user's OTHER tabs (also ringing this same call) to dismiss it.
      // `broadcast=false` when we're already reacting to a sibling's broadcast.
      if (broadcast) {
        try { callChannelRef.current?.postMessage({ type: 'call-resolved', callId: resolvedId }); } catch { /* noop */ }
      }
    }
    // Kill any pending disconnect grace timer so it can never fire after the
    // call has already been torn down.
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    peerRef.current?.close();
    peerRef.current = null;
    sfuMediaRef.current?.close();
    sfuMediaRef.current = null;
    directSfuRef.current = false;
    setVideoReady(false);
    setCameraBusy(false);
    setLocalVideoTrack(null);
    setRemoteVideoTrack(null);
    setRemoteScreenTrack(null);
    setScreenSharing(false);
    setScreenShareBusy(false);
    localCandidatesRef.current = [];
    postedCandidateKeysRef.current = new Set();
    pendingCandidates.current = [];
    remoteDescriptionAppliedRef.current = false;
    seenCandidateKeysRef.current = new Set();
    localAnswerRef.current = null;
    answerPostedRef.current = false;
    answerSentRef.current = false;
    pendingOffer.current = null;
    routeRef.current = null;
    callMetaRef.current = null;
    pendingActionRef.current = null;
    unloadActionRef.current = null;
    isCallerRef.current = false;
    peerInvitedRef.current = false;
    answerAppliedRef.current = false;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    if (nativeCallIdRef.current) {
      const pendingId = nativeCallIdRef.current;
      nativeCallIdRef.current = null;
      void clearNativeIncomingCall(pendingId);
    }
    speakingRef.current?.close();
    speakingRef.current = null;
    setRemoteSpeaking(false);
    setAddingOpen(false);   // never let an open "Add" sheet leak into the next call
    setCall(reason ? { ...IDLE, status: 'ended', endReason: reason } : IDLE);
    if (reason) {
      // brief "ended" flash, then back to idle
      setTimeout(() => { setCall(IDLE); setMinimized(false); }, 1500);
    } else {
      setMinimized(false);
    }
  }, [logCallOutcome]);

  const storeSnapshot = useCallback((next: CallSnapshot): boolean => {
    const previous = snapshotRef.current;
    if (previous?.callId === next.callId && previous.version >= next.version) return false;
    snapshotRef.current = next;
    setSnapshot(next);
    return true;
  }, []);

  const reconcileSnapshot = useCallback((next: CallSnapshot, actorUserId: string | null = null) => {
    const tag = `call=${next.callId} v=${next.version} ${next.status}`;
    const known = snapshotRef.current;
    if (!storeSnapshot(next)) {
      logCallDiagnostic(`drop ${tag}: already at v${known?.version}`);
      return;
    }
    const callee = isCallerRef.current && routeRef.current?.callId === next.callId ? routeRef.current.peerUserId : null;
    if (callee) {
      const calleeStatus = next.participantStatuses?.[callee];
      if (calleeStatus === 'invited') peerInvitedRef.current = true;
      if (calleeReportedRinging({ actorUserId, calleeSeenInvited: peerInvitedRef.current, calleeStatus, calleeUserId: callee })) {
        // The callee's "alerted" receipt; it stays once the call moves on.
        setCall((current) => current.callId === next.callId && !current.peerRinging
          ? { ...current, peerRinging: true }
          : current);
      }
    }
    if (routeRef.current?.callId !== next.callId) {
      logCallDiagnostic(`stored ${tag}: not this window's call (route=${routeRef.current ? 'other call' : 'none'}, ui=${statusRef.current})`);
      // A direct SFU ring has no call-invite frame: its call-state is the ring.
      if (!routeRef.current && next.status === 'ringing' && isDirectSfuSnapshot(next)) {
        void openDirectIncomingRef.current(next);
      }
      return;
    }

    if (next.status === 'active') {
      if (
        statusRef.current === 'incoming'
        && pendingActionRef.current?.eventId !== next.eventId
      ) {
        // Another device/tab answered this ring. It owns media; this renderer
        // dismisses silently instead of opening a connecting surface with no peer.
        logCallDiagnostic(`${tag} while ringing here: answered elsewhere, dismissing`);
        cleanup(null, true, 'none');
        return;
      }
      logCallDiagnostic(`${tag} ui=${statusRef.current}${pendingActionRef.current?.eventId === next.eventId ? ' (own answer)' : ''}`);
      if (isCallerRef.current && statusRef.current === 'outgoing') {
        logCallLifecycle('CALL_ACCEPTED', next.callId, {
          peer: routeRef.current?.peerUserId, role: 'caller', version: next.version, state: 'connecting',
        });
      }
      setCall((current) => current.callId === next.callId && current.status !== 'connected'
        ? { ...current, status: 'connecting' }
        : current);
      return;
    }
    if (next.status === 'ringing') return;
    if (pendingActionRef.current?.callId === next.callId && pendingActionRef.current.eventId === next.eventId) {
      logCallDiagnostic(`${tag}: own pending action, its caller cleans up`);
      return;
    }

    if (next.reason === 'continued_in_group') {
      // Add people moved this call into a group: no "Call ended", the group takes over.
      logCallDiagnostic(`${tag}: continued in a group call`);
      cleanup(null, true, 'completed');
      return;
    }
    const reason = next.status === 'declined'
      ? 'Call declined'
      : next.status === 'cancelled'
        ? 'Call cancelled'
      : next.status === 'missed'
        ? callMetaRef.current?.direction === 'incoming' ? 'Missed call' : 'No answer'
        : 'Call ended';
    cleanup(reason);
  }, [cleanup, storeSnapshot]);

  // The group call that continues this 1:1 ends it here, quietly.
  useEffect(() => {
    bindDirectCallRelease((callId) => {
      if (routeRef.current?.callId === callId) cleanup(null, true, 'completed');
    });
    return () => bindDirectCallRelease(null);
  }, [cleanup]);

  const handleCallState = useCallback((event: CallEventEnvelope) => {
    if (
      event.callId !== event.snapshot.callId
      || event.sequenceNumber !== event.snapshot.version
      || event.eventId !== event.snapshot.eventId
    ) {
      logCallDiagnostic(`drop call=${event.callId} v=${event.sequenceNumber}: envelope does not match its snapshot`);
      return;
    }

    const previous = snapshotRef.current;
    if (previous?.callId === event.callId && event.snapshot.version > previous.version + 1) {
      logCallDiagnostic(`gap call=${event.callId}: at v${previous.version}, got v${event.snapshot.version}; refetching`);
      void getCallSnapshot(event.callId).then(reconcileSnapshot).catch((error) => {
        // The next state frame retries.
        logCallDiagnostic(`gap refetch failed call=${event.callId}: ${describeCallError(error)}`);
      });
      return;
    }
    reconcileSnapshot(event.snapshot, event.senderUserId);
  }, [reconcileSnapshot]);

  const applyAuthoritativeAction = useCallback(async (
    action: CallLifecycleAction,
  ): Promise<CallSnapshot | null> => {
    const route = routeRef.current;
    if (!route) return null;

    let current = snapshotRef.current?.callId === route.callId ? snapshotRef.current : null;
    const eventId = createCallEventId(`call-${action}`);
    try {
      current ??= await getCallSnapshot(route.callId);
      if (current.callId !== route.callId) return null;
      storeSnapshot(current);
      if (isTerminalSnapshot(current.status)) {
        logCallDiagnostic(`action ${action} call=${route.callId} skipped: already ${current.status} v${current.version}`);
        return current;
      }

      pendingActionRef.current = { callId: route.callId, eventId };
      const next = await actOnCall(route.callId, {
        action, eventId, expectedVersion: current.version,
      });
      // A stale expectedVersion is not an error: the server answers with the
      // current snapshot unchanged, which this line makes visible.
      logCallDiagnostic(
        `action ${action} call=${route.callId} at v${current.version} -> ${next.status} v${next.version}`,
      );
      storeSnapshot(next);
      return next.callId === route.callId ? next : null;
    } catch (error: any) {
      logCallDiagnostic(`action ${action} call=${route.callId} failed: ${describeCallError(error)}`);
      if (error?.response?.status !== 409) return null;
      try {
        const latest = await getCallSnapshot(route.callId);
        if (latest.callId !== route.callId) return null;
        storeSnapshot(latest);
        const terminal = isTerminalSnapshot(latest.status);
        if (terminal || (action === 'answer' && latest.status === 'active')) return latest;
        pendingActionRef.current = { callId: route.callId, eventId };
        const retried = await actOnCall(route.callId, {
          action, eventId, expectedVersion: latest.version,
        });
        logCallDiagnostic(
          `action ${action} call=${route.callId} retried at v${latest.version} -> ${retried.status} v${retried.version}`,
        );
        storeSnapshot(retried);
        return retried.callId === route.callId ? retried : null;
      } catch (retryError) {
        logCallDiagnostic(`action ${action} call=${route.callId} retry failed: ${describeCallError(retryError)}`);
        return null;
      }
    } finally {
      if (pendingActionRef.current?.eventId === eventId) pendingActionRef.current = null;
    }
  }, [storeSnapshot]);

  /**
   * Join a direct SFU call's LiveKit room, then post `join` and
   * `media-connected` (never `accept` for the caller, and no P2P invite or
   * SDP). The caller does this while still ringing; the call goes active when
   * the callee accepts, and "connected" is the peer's audio arriving. Throws
   * when the room or the `join` fails; the caller of this decides the action.
   */
  const connectDirectSfu = useCallback(async (callId: string, muted: boolean): Promise<void> => {
    const peerUserId = routeRef.current?.peerUserId;
    const role = isCallerRef.current ? 'caller' : 'callee';
    const media = new DirectSfuMedia({
      protectIpAddress,
      audioElement: remoteAudioRef.current,
      outputDeviceId: selectedAudioOutputId,
    });
    sfuMediaRef.current = media;
    const isCurrent = () => sfuMediaRef.current === media && routeRef.current?.callId === callId;
    const clearGrace = () => {
      if (!disconnectTimerRef.current) return;
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    };
    const leaveAfterFailure = () => {
      const action = terminalActionFor(isCallerRef.current, isRinging(statusRef.current), true);
      void applyAuthoritativeAction(action).then(() => cleanup('Connection lost'));
    };

    media.onRemoteStream = (stream) => {
      if (!isCurrent()) return;
      clearGrace();
      if (statusRef.current !== 'connected') {
        logCallLifecycle('TRACK_SUBSCRIBED', callId, { peer: peerUserId, kind: 'audio', via: 'sfu' });
        logCallLifecycle('CALL_CONNECTED', callId, { peer: peerUserId, role, via: 'sfu' });
      }
      if (callMetaRef.current && callMetaRef.current.answeredAt == null) {
        callMetaRef.current.answeredAt = Date.now();
        noteOnDirectCall(callMetaRef.current.callId);
      }
      stopAllCallSounds();
      setCall((c) => (c.callId !== callId || c.status === 'connected'
        ? c
        : { ...c, status: 'connected', startedAt: Date.now() }));
      if (stream) {
        if (!speakingRef.current) {
          speakingRef.current = new SpeakingMonitor((_uid, speaking) => setRemoteSpeaking(speaking));
        }
        speakingRef.current.add('peer', stream);
      }
    };
    // The server's `leave` normally ends a direct call for both sides before
    // this matters; it covers a peer that vanished without one (crash, lost
    // network). Same grace as a P2P 'disconnected', cancelled if they return.
    media.onPeerLeft = () => {
      if (!isCurrent() || disconnectTimerRef.current) return;
      disconnectTimerRef.current = setTimeout(() => {
        disconnectTimerRef.current = null;
        if (isCurrent()) leaveAfterFailure();
      }, DISCONNECT_GRACE_MS);
    };
    media.onRemoteVideo = (track) => {
      if (isCurrent()) setRemoteVideoTrack(track);
    };
    media.onLocalVideo = (track) => {
      if (isCurrent()) setLocalVideoTrack(track);
    };
    media.onRemoteScreen = (track) => {
      if (isCurrent()) setRemoteScreenTrack(track);
    };
    media.onLocalScreen = (sharing) => {
      if (isCurrent()) setScreenSharing(sharing);
    };
    media.onConnectionStateChange = (state) => {
      if (!isCurrent()) return;
      logCallLifecycle('SFU_CONNECTION', callId, { peer: peerUserId, role, state });
      if (state === 'reconnecting') {
        void applyAuthoritativeAction('media-reconnecting');
      } else if (state === 'connected') {
        void applyAuthoritativeAction('media-connected');
      } else {
        clearGrace();
        void applyAuthoritativeAction('media-disconnected');
        leaveAfterFailure();
      }
    };

    try {
      const credentials = await getLiveKitCredentials(callId);
      if (credentials.callId !== callId) throw new Error('LiveKit credentials are for another call');
      if (!isCurrent()) throw new Error('Direct call changed while connecting');
      // Without the app's TURN list relay-only users could not reach the SFU;
      // with no list, fall back to LiveKit's own ICE servers.
      const iceServers = await Promise.resolve().then(() => getIceServers()).catch(() => undefined);
      if (!isCurrent()) throw new Error('Direct call changed while connecting');
      await media.connect(credentials, muted, iceServers as RTCIceServer[] | undefined);
      if (!isCurrent()) throw new Error('Direct call changed while connecting');
      setVideoReady(true);
      logCallLifecycle('TRACK_PUBLISHED', callId, { peer: peerUserId, kind: 'audio', via: 'sfu' });
      const joined = await applyAuthoritativeAction('join');
      if (!joined || isTerminalSnapshot(joined.status)) throw new Error('Direct call join was not recorded');
      await applyAuthoritativeAction('media-connected');
    } catch (error) {
      if (sfuMediaRef.current === media) {
        sfuMediaRef.current = null;
        setVideoReady(false);
      }
      media.close();
      throw error;
    }
  }, [applyAuthoritativeAction, cleanup, protectIpAddress, selectedAudioOutputId]);

  const wirePeer = useCallback((peer: PeerService) => {
    peer.onIceCandidate = (candidate) => {
      localCandidatesRef.current.push(candidate);
      if (!isCallerRef.current && !answerSentRef.current) return;
      sendFrame('ice-candidate', { candidate });
    };
    peer.onRemoteStream = (stream) => {
      logCallLifecycle('TRACK_SUBSCRIBED', routeRef.current?.callId, {
        peer: routeRef.current?.peerUserId, kind: 'audio',
      });
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream;
        void remoteAudioRef.current.play().catch(() => {});
        void peer.setOutputSink(remoteAudioRef.current, selectedAudioOutputId);
      }
      // Watch the peer's loudness so their avatar shows a speaking wave.
      if (!speakingRef.current) {
        speakingRef.current = new SpeakingMonitor((_uid, speaking) => setRemoteSpeaking(speaking));
      }
      speakingRef.current.add('peer', stream);
    };
    peer.onConnectionStateChange = (state) => {
      const route = routeRef.current;
      logCallLifecycle('PEER_CONNECTION', route?.callId, {
        peer: route?.peerUserId, role: isCallerRef.current ? 'caller' : 'callee', state,
      });
      if (state === 'connected') {
        if (statusRef.current !== 'connected') {
          logCallLifecycle('MEDIA_CONNECTED', route?.callId, { peer: route?.peerUserId, state });
          logCallLifecycle('CALL_CONNECTED', route?.callId, {
            peer: route?.peerUserId, role: isCallerRef.current ? 'caller' : 'callee',
            remoteCandidates: seenCandidateKeysRef.current.size,
          });
        }
        // Connected (or recovered from a transient blip): cancel any pending
        // disconnect grace timer so it can't tear the live call down.
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        if (callMetaRef.current && callMetaRef.current.answeredAt == null) {
          callMetaRef.current.answeredAt = Date.now();
          noteOnDirectCall(callMetaRef.current.callId);
        }
        setCall((c) => (c.status === 'connected' ? c : { ...c, status: 'connected', startedAt: Date.now() }));
        void applyAuthoritativeAction('media-connected');
      } else if (state === 'failed' || state === 'closed') {
        // A non-recovering drop — terminal, tear down at once.
        logCallLifecycle('CALL_FAILED', route?.callId, {
          peer: route?.peerUserId, reason: `peer-${state}`,
          answerApplied: answerAppliedRef.current, remoteCandidates: seenCandidateKeysRef.current.size,
        });
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        if (routeRef.current) {
          void applyAuthoritativeAction('media-disconnected');
          const action = terminalActionFor(isCallerRef.current, isRinging(statusRef.current));
          void applyAuthoritativeAction(action).then(() => cleanup('Connection lost'));
        }
      } else if (state === 'disconnected') {
        // 'disconnected' is frequently TRANSIENT — a brief network blip or
        // Wi-Fi handoff commonly bounces a healthy call here and it recovers
        // on its own within a few seconds. Don't kill the call immediately;
        // arm a short grace timer instead. A return to 'connected' cancels it
        // above; a move to 'failed'/'closed' tears down at once; only a
        // disconnect that never recovers reaches this callback and tears down.
        // Leave the UI on 'connected' meanwhile so a momentary blip doesn't
        // flicker the call screen.
        if (!disconnectTimerRef.current) {
          void applyAuthoritativeAction('media-reconnecting');
          disconnectTimerRef.current = setTimeout(() => {
            disconnectTimerRef.current = null;
            if (routeRef.current) {
              const action = terminalActionFor(isCallerRef.current, isRinging(statusRef.current));
              void applyAuthoritativeAction(action).then(() => cleanup('Connection lost'));
            }
          }, DISCONNECT_GRACE_MS);
        }
      }
    };
  }, [sendFrame, cleanup, applyAuthoritativeAction, selectedAudioOutputId]);

  const flushCandidates = useCallback(async () => {
    const peer = peerRef.current;
    if (!peer || !remoteDescriptionAppliedRef.current) return;
    const queued = pendingCandidates.current;
    pendingCandidates.current = [];
    for (const c of queued) await peer.addIceCandidate(c);
  }, []);

  // A candidate is only valid against an applied remote description: added
  // earlier, the browser rejects it and PeerService swallows the error, so a
  // callee's candidates that beat its answer here were silently lost.
  const addRemoteCandidate = useCallback((candidate: RTCIceCandidateInit | null | undefined) => {
    if (!candidate) return;
    const key = candidateKey(candidate);
    if (key) {
      if (seenCandidateKeysRef.current.has(key)) return;
      seenCandidateKeysRef.current.add(key);
    }
    const peer = peerRef.current;
    if (peer && remoteDescriptionAppliedRef.current) void peer.addIceCandidate(candidate);
    else if (pendingCandidates.current.length < MAX_BUFFERED_CANDIDATES) pendingCandidates.current.push(candidate);
  }, []);

  // Caller only, once: the answer from the socket or from recovery, whichever
  // comes first, after its sender's session was verified as answered.
  const applyRemoteAnswer = useCallback((callId: string, sdp: RTCSessionDescriptionInit, via: 'socket' | 'rest') => {
    const route = routeRef.current;
    if (!route || route.callId !== callId || !isCallerRef.current || answerAppliedRef.current) return false;
    answerAppliedRef.current = true;
    stopAllCallSounds();
    logCallLifecycle('CALL_ANSWER_RECEIVED', callId, { peer: route.peerUserId, role: 'caller', via });
    for (const candidate of localCandidatesRef.current) sendFrame('ice-candidate', { candidate });
    const peer = peerRef.current;
    void peer?.setRemoteDescription(sdp).then(() => {
      if (peerRef.current !== peer) return undefined;
      remoteDescriptionAppliedRef.current = true;
      return flushCandidates();
    }).catch((error) => {
      logCallDiagnostic(`answer call=${callId} not applied: ${describeCallError(error)}`);
    });
    setCall((current) => current.status === 'outgoing' ? { ...current, status: 'connecting' } : current);
    return true;
  }, [flushCandidates, sendFrame]);

  // The callee's answer over HTTPS: the response proves the server stored it
  // (so the caller can recover it) and relayed it. A socket send proves nothing.
  const postAnswer = useCallback(async (callId: string, sdp: RTCSessionDescriptionInit) => {
    try {
      await postCallSignals(callId, [{ type: 'call-answer', sdp }]);
      if (routeRef.current?.callId === callId) answerPostedRef.current = true;
      logCallLifecycle('CALL_ANSWER_SENT', callId, { peer: routeRef.current?.peerUserId, via: 'rest' });
      return true;
    } catch (error) {
      logCallDiagnostic(`answer post call=${callId} failed: ${describeCallError(error)}`);
      return false;
    }
  }, []);

  // Pull what this side may have missed, and re-post what the peer may have
  // missed. Safe to repeat: the answer applies once, candidates dedupe on both
  // ends, and the server stores each candidate once.
  const recoverSignaling = useCallback(async (reason: string) => {
    const route = routeRef.current;
    if (!route || signalingRecoveryInFlightRef.current) return;
    signalingRecoveryInFlightRef.current = true;
    try {
      const answer = localAnswerRef.current;
      if (!isCallerRef.current && answer && !answerPostedRef.current) await postAnswer(route.callId, answer);
      const unposted = localCandidatesRef.current.filter((candidate) => {
        const key = candidateKey(candidate);
        return key !== null && !postedCandidateKeysRef.current.has(key);
      });
      if (unposted.length) {
        try {
          await postCallSignals(route.callId, unposted.map((candidate) => ({ type: 'ice-candidate' as const, candidate })));
          for (const candidate of unposted) postedCandidateKeysRef.current.add(candidateKey(candidate)!);
        } catch (error) {
          logCallDiagnostic(`signaling repost call=${route.callId} failed: ${describeCallError(error)}`);
        }
      }

      const signaling = await getCallSignaling(route.callId);
      if (routeRef.current?.callId !== route.callId) return;
      let recoveredAnswer = false;
      if (isCallerRef.current && !answerAppliedRef.current && signaling.answer?.sdp?.type === 'answer') {
        const known = snapshotRef.current?.callId === route.callId ? snapshotRef.current : null;
        const authorized = known?.status === 'active' ? known : await getCallSnapshot(route.callId);
        if (authorized.callId === route.callId && authorized.status === 'active') {
          storeSnapshot(authorized);
          recoveredAnswer = applyRemoteAnswer(route.callId, signaling.answer.sdp, 'rest');
        }
      }
      const known = seenCandidateKeysRef.current.size;
      for (const item of signaling.candidates ?? []) addRemoteCandidate(item.candidate);
      const recoveredCandidates = seenCandidateKeysRef.current.size - known;
      if (recoveredAnswer || recoveredCandidates) {
        logCallLifecycle('SIGNALING_RECOVERED', route.callId, {
          peer: route.peerUserId, reason, answer: recoveredAnswer, candidates: recoveredCandidates,
        });
      }
    } catch (error) {
      logCallDiagnostic(`signaling recovery call=${route.callId} reason=${reason} failed: ${describeCallError(error)}`);
    } finally {
      signalingRecoveryInFlightRef.current = false;
    }
  }, [addRemoteCandidate, applyRemoteAnswer, postAnswer, storeSnapshot]);

  const startCall = useCallback(async (
    conversationId: string,
    peerUserId: string,
    peerName: string | null = null,
    peerAvatarUrl: string | null = null,
  ) => {
    if (call.status !== 'idle' || startingCallRef.current || isOtherCallActive('oneToOne')) return;
    startingCallRef.current = true;
    let authoritative: CallSnapshot;
    try {
      // Advertise direct SFU: with recording on, the server routes this 1:1
      // call through LiveKit so it can be recorded.
      authoritative = await startAuthoritativeCall({
        clientEventId: createCallEventId('call-start'),
        conversationId,
        participantUserIds: [peerUserId],
        callType: 'voice',
      }, { directSfu: true });
    } catch {
      return;
    } finally {
      startingCallRef.current = false;
    }
    const direct = isDirectSfuSnapshot(authoritative);
    if (
      authoritative.status !== 'ringing'
      || authoritative.conversationId !== conversationId
      || !(direct || (authoritative.mediaTopology === 'p2p' && !authoritative.isGroup))
      || !authoritative.participantUserIds.includes(peerUserId)
    ) {
      logCallDiagnostic(
        `start call=${authoritative.callId} refused: ${authoritative.status} ${authoritative.mediaTopology} `
        + `participants=${authoritative.participantUserIds.length}`,
      );
      // Never leave a session this side created ringing with no UI behind it.
      if (authoritative.status === 'ringing') {
        void actOnCall(authoritative.callId, {
          action: 'cancel', eventId: createCallEventId('call-cancel'), expectedVersion: authoritative.version,
        }).catch(() => { /* it expires server-side */ });
      }
      return;
    }

    const callId = authoritative.callId;
    storeSnapshot(authoritative);
    setCallActive('oneToOne', true);
    routeRef.current = { callId, conversationId, peerUserId };
    unloadActionRef.current = null;
    callMetaRef.current = {
      callId, peerUserId, conversationId,
      direction: 'outgoing', ringStartedAt: Date.now(), answeredAt: null,
    };
    isCallerRef.current = true;
    peerInvitedRef.current = authoritative.participantStatuses?.[peerUserId] === 'invited';
    directSfuRef.current = direct;
    answerAppliedRef.current = false;
    remoteDescriptionAppliedRef.current = false;
    seenCandidateKeysRef.current = new Set();
    postedCandidateKeysRef.current = new Set();
    localCandidatesRef.current = [];
    setMinimized(false);
    setCall({ ...IDLE, muted: audioMutedByDefault, status: 'outgoing', callId, conversationId, peerUserId, peerName, peerAvatarUrl });
    logCallLifecycle('CALL_CREATED', callId, {
      peer: peerUserId, role: 'caller', version: authoritative.version, state: 'ringing', media: direct ? 'sfu' : 'p2p',
    });
    if (direct) {
      try {
        await connectDirectSfu(callId, audioMutedByDefault);
        logCallLifecycle('CALL_RINGING', callId, { peer: peerUserId, role: 'caller', via: 'sfu' });
      } catch (error) {
        logCallDiagnostic(`direct call=${callId} could not connect: ${describeCallError(error)}`);
        if (routeRef.current?.callId !== callId) return;
        // The server's view decides cancel vs leave: the callee may have
        // accepted while this side was still connecting.
        const known = snapshotRef.current?.callId === callId ? snapshotRef.current : authoritative;
        const action = terminalActionFor(true, known.status === 'ringing', true);
        void applyAuthoritativeAction(action).then(() => cleanup('Call failed', true, 'failed'));
      }
      return;
    }
    try {
      const iceServers = await getIceServers();
      const peer = new PeerService(iceServers, { protectIpAddress });
      peerRef.current = peer;
      wirePeer(peer);
      await peer.initLocalAudio();
      logCallLifecycle('TRACK_PUBLISHED', callId, { peer: peerUserId, kind: 'audio' });
      const offer = await peer.createOffer();
      sendFrame('call-invite', { sdp: offer, media: 'audio' });
      logCallLifecycle('CALL_RINGING', callId, { peer: peerUserId, role: 'caller', via: 'socket' });
    } catch {
      // Mic blocked/denied or SDP setup failed. Without this, status stays
      // 'outgoing' forever and the ringback loop never stops.
      void applyAuthoritativeAction('cancel').then(() => cleanup('Microphone unavailable'));
    }
  }, [
    call.status, wirePeer, sendFrame, cleanup, storeSnapshot, applyAuthoritativeAction, connectDirectSfu,
    protectIpAddress, audioMutedByDefault,
  ]);

  const accept = useCallback(async () => {
    if (call.status !== 'incoming' || !routeRef.current || isOtherCallActive('oneToOne')) return;
    const accepted = await applyAuthoritativeAction('answer');
    if (!accepted || accepted.status !== 'active') {
      if (accepted && accepted.status !== 'ringing') {
        const reason = accepted.status === 'missed' ? 'Missed call'
          : accepted.status === 'declined' ? 'Call declined' : 'Call ended';
        cleanup(reason);
      }
      return;
    }
    // Answering marks this account busy so a concurrent group call is refused.
    setCallActive('oneToOne', true);
    // Stop the ringtone SYNCHRONOUSLY (don't wait on the status effect), and
    // tell sibling tabs ringing this same call to dismiss it — we answered here.
    stopAllCallSounds();
    try { callChannelRef.current?.postMessage({ type: 'call-resolved', callId: routeRef.current.callId }); } catch { /* noop */ }
    setCall((c) => ({ ...c, muted: audioMutedByDefault || c.muted, status: 'connecting' }));
    const route = routeRef.current;
    logCallLifecycle('CALL_ACCEPTED', route.callId, {
      peer: route.peerUserId, role: 'callee', version: accepted.version, state: 'connecting',
    });
    if (directSfuRef.current) {
      try {
        await connectDirectSfu(route.callId, audioMutedByDefault);
      } catch (error) {
        logCallDiagnostic(`direct call=${route.callId} could not connect: ${describeCallError(error)}`);
        if (routeRef.current?.callId !== route.callId) return;
        void applyAuthoritativeAction('leave').then(() => cleanup('Call failed', true, 'failed'));
      }
      return;
    }
    try {
      const iceServers = await getIceServers();
      const peer = new PeerService(iceServers, { protectIpAddress });
      peerRef.current = peer;
      wirePeer(peer);
      await peer.initLocalAudio();
      logCallLifecycle('TRACK_PUBLISHED', route.callId, { peer: route.peerUserId, kind: 'audio' });
      peer.setMuted(audioMutedByDefault);
      if (pendingOffer.current) {
        await peer.setRemoteDescription(pendingOffer.current);
        remoteDescriptionAppliedRef.current = true;
      }
      await flushCandidates();
      const answer = await peer.createAnswer();
      const sdp = { type: answer.type, sdp: answer.sdp };
      localAnswerRef.current = sdp;
      // Acknowledged and stored: the caller can recover it even when the
      // socket relay to it is lost.
      if (!(await postAnswer(route.callId, sdp))) {
        // A server without the signaling endpoint still relays the socket
        // frame; recovery re-posts it while media connects.
        sendFrame('call-answer', { sdp });
        logCallLifecycle('CALL_ANSWER_SENT', route.callId, { peer: route.peerUserId, via: 'socket' });
      }
      if (routeRef.current?.callId !== route.callId) return;
      answerSentRef.current = true;
      for (const candidate of localCandidatesRef.current) sendFrame('ice-candidate', { candidate });
      // The caller's candidates sent while this side was still ringing (or
      // while its socket was down) were dropped here; the server kept them.
      void getCallSignaling(route.callId).then((signaling) => {
        if (routeRef.current?.callId !== route.callId) return;
        for (const item of signaling.candidates ?? []) addRemoteCandidate(item.candidate);
      }).catch((error) => {
        logCallDiagnostic(`signaling pull call=${route.callId} failed: ${describeCallError(error)}`);
      });
    } catch {
      // Mic blocked/denied or SDP setup failed after Accept. Commit the
      // terminal state so call-state tears down every participant reliably.
      // The callee leaves: only the caller may end a direct call.
      void applyAuthoritativeAction('leave').then(() => {
        cleanup('Microphone unavailable');
      });
    }
  }, [
    call.status, wirePeer, sendFrame, flushCandidates, addRemoteCandidate, postAnswer, cleanup, applyAuthoritativeAction,
    connectDirectSfu, protectIpAddress, audioMutedByDefault,
  ]);

  const reject = useCallback(() => {
    if (call.status !== 'incoming') return;
    void applyAuthoritativeAction('decline').then((declined) => {
      // The v2 server reports a declined direct call as `ended`, not `declined`.
      if (!declined || !isTerminalSnapshot(declined.status)) return;
      cleanup(null, true, declined.status === 'missed' ? 'missed' : 'declined');
    });
  }, [call.status, cleanup, applyAuthoritativeAction]);

  const hangup = useCallback(() => {
    if (call.status === 'idle' || call.status === 'ended') return;
    const action = terminalActionFor(isCallerRef.current, isRinging(call.status), directSfuRef.current);
    void applyAuthoritativeAction(action).then((ended) => {
      if (!ended || !isTerminalSnapshot(ended.status)) return;
      const outcome: CallOutcome =
        call.status === 'outgoing' ? (ended.status === 'missed' ? 'no_answer' : 'cancelled')
          : call.status === 'incoming' ? (ended.status === 'missed' ? 'missed' : 'declined')
            : callMetaRef.current?.answeredAt ? 'completed' : 'failed';
      cleanup(null, true, outcome);
    });
  }, [call.status, cleanup, applyAuthoritativeAction]);

  const toggleMute = useCallback(() => {
    setCall((c) => {
      const muted = !c.muted;
      peerRef.current?.setMuted(muted);
      void sfuMediaRef.current?.setMuted(muted).catch(() => {
        toast.error('Could not update the microphone. Check device permissions and try again.');
      });
      return { ...c, muted };
    });
  }, []);

  const toggleVideo = useCallback(() => {
    const media = sfuMediaRef.current;
    if (!media || cameraBusy) return;
    const callId = routeRef.current?.callId;
    const enable = !localVideoTrack;
    setCameraBusy(true);
    void media.setCameraEnabled(enable).then(() => {
      logCallLifecycle('CAMERA', callId, { state: enable ? 'on' : 'off', via: 'sfu' });
    }, (error) => {
      // The self-view already reports the camera as it is (off when it
      // never started); only the reason is left to show.
      logCallDiagnostic(`camera call=${callId ?? '-'} ${enable ? 'on' : 'off'} failed: ${describeCallError(error)}`);
      if (sfuMediaRef.current === media) toast.error(cameraErrorMessage(error));
    }).finally(() => {
      if (sfuMediaRef.current === media) setCameraBusy(false);
    });
  }, [cameraBusy, localVideoTrack]);

  const toggleScreenShare = useCallback(() => {
    const media = sfuMediaRef.current;
    if (!media || screenShareBusy) return;
    const callId = routeRef.current?.callId;
    const enable = !screenSharing;
    setScreenShareBusy(true);
    void media.setScreenShareEnabled(enable).then(() => {
      logCallLifecycle('SCREEN_SHARE', callId, { state: enable ? 'on' : 'off', via: 'sfu' });
    }, (error) => {
      logCallDiagnostic(`screen share call=${callId ?? '-'} ${enable ? 'on' : 'off'} failed: ${describeCallError(error)}`);
      const message = enable ? screenShareErrorMessage(error) : null;
      if (message && sfuMediaRef.current === media) toast.error(message);
    }).finally(() => {
      if (sfuMediaRef.current === media) setScreenShareBusy(false);
    });
  }, [screenShareBusy, screenSharing]);

  const toggleMinimize = useCallback(() => {
    setMinimized((m) => !m);
  }, []);

  const minimize = useCallback(() => {
    setMinimized(true);
  }, []);

  // Drive call sounds straight off the state machine, so loops always start and
  // stop with the call and can never leak. Ringtone (incoming) and ringback
  // (outgoing) loop; connect/end are one-shots. All playback is best-effort.
  useEffect(() => {
    switch (call.status) {
      case 'incoming': startRingtone(); break;
      case 'outgoing': startRingback(); break;
      case 'connected': stopAllCallSounds(); playConnectTone(); break;
      case 'ended': stopAllCallSounds(); playEndTone(); break;
      default: stopAllCallSounds(); break; // idle, connecting
    }
  }, [call.status]);

  // Safety net: stop every loop (and leave any LiveKit room) if the provider
  // unmounts mid-call.
  useEffect(() => () => {
    stopAllCallSounds();
    sfuMediaRef.current?.close();
  }, []);

  // Desktop app: ring with the OS ringtone (macOS FaceTime tone / Windows
  // phone ring) instead of the synthesized bell. Best-effort — outside
  // Electron or on any failure the synthesized ringtone stays.
  useEffect(() => {
    let cancelled = false;
    void getSystemRingtone().then((tone) => {
      if (!cancelled && tone) setSystemRingtone(tone.data);
    });
    return () => { cancelled = true; };
  }, []);

  // Surface a ringing call as an always-on-top desktop popup (WhatsApp-style
  // call banner with Accept/Decline), so it's seen — and answerable — while
  // the window is hidden in the tray or behind other apps. The renderer
  // always notifies; MAIN decides visibility (popup suppressed while the
  // window is focused — the CallScreen is the ring UI there — and synced to
  // focus/blur for the whole ring). Withdrawn the moment the call leaves
  // 'incoming' (answered here or in a sibling tab, declined, cancelled,
  // timed out). If it ended UNANSWERED, leave a regular "missed call"
  // notification that deep-links into the conversation.
  const notifiedCallRef = useRef<{
    callId: string; peerName: string | null; conversationId: string | null; avatarUrl: string | null;
  } | null>(null);
  useEffect(() => {
    if (call.status === 'incoming' && call.callId) {
      // One popup per ring — but when the avatar back-fill resolves a photo
      // for the SAME call, notify again: main patches the live popup in
      // place (never re-creates it), so the photo pops in without a flash.
      const avatarUrl = call.peerAvatarUrl ?? null;
      const prev = notifiedCallRef.current;
      if (prev?.callId === call.callId && prev.avatarUrl === avatarUrl) return;
      notifiedCallRef.current = {
        callId: call.callId, peerName: call.peerName, conversationId: call.conversationId, avatarUrl,
      };
      void showCallNotification({
        callId: call.callId,
        title: call.peerName || 'Incoming call',
        body: 'TeamLio audio call',
        iconUrl: avatarUrl,
      });
      return;
    }
    const notified = notifiedCallRef.current;
    if (!notified) return;
    notifiedCallRef.current = null;
    void closeCallNotification(notified.callId);
    // cleanup() reasons for a ring that died unanswered: 45s timeout ('Missed
    // call') or the caller hung up while we rang ('Call cancelled').
    if (call.status === 'ended' && (call.endReason === 'Missed call' || call.endReason === 'Call cancelled')) {
      void showDesktopNotification({
        title: notified.peerName || 'TeamLio',
        body: 'Missed voice call',
        conversationId: notified.conversationId,
      });
    }
  }, [call.status, call.callId, call.peerName, call.peerAvatarUrl, call.conversationId, call.endReason]);

  // Mirror of the sounds safety net: if this renderer goes away mid-ring
  // (provider unmount, reload, auto-update relaunch), withdraw the OS
  // notification too — the fresh renderer has no memory of it and could
  // never close it. Main's TTL is the last-resort backstop.
  useEffect(() => () => {
    if (notifiedCallRef.current) void closeCallNotification(notifiedCallRef.current.callId);
  }, []);

  // Latest accept/reject for the mount-once notification-action subscription
  // below (both callbacks are recreated whenever call.status changes).
  const acceptRef = useRef(accept);
  acceptRef.current = accept;
  const rejectRef = useRef(reject);
  rejectRef.current = reject;

  // React to the notification's buttons: Accept answers right away (main has
  // already focused the window), Decline rejects. A plain body click or
  // dismiss needs nothing here — the ringing CallScreen is the UI.
  useEffect(() => {
    return onCallNotificationAction(({ callId, action }) => {
      if (!callId || routeRef.current?.callId !== callId) return;
      if (statusRef.current !== 'incoming') return;
      if (action === 'accept') void acceptRef.current();
      else if (action === 'decline') rejectRef.current();
    });
  }, []);

  // Hard timeout for the ringing states. Guarantees the ring can NEVER loop
  // forever if the authoritative teardown is delayed — e.g. the caller lost
  // connectivity while closing/refreshing. Re-runs on every
  // status change, so it only counts down while actually ringing.
  useEffect(() => {
    if (call.status !== 'incoming' && call.status !== 'outgoing') return;
    const wasOutgoing = call.status === 'outgoing';
    const id = setTimeout(() => {
      void applyAuthoritativeAction(terminalActionFor(wasOutgoing, true)).then((terminal) => {
        if (!terminal || !isTerminalSnapshot(terminal.status)) return;
        cleanup(
          wasOutgoing ? 'No answer' : 'Missed call',
          true,
          wasOutgoing ? 'no_answer' : 'missed',
        );
      });
    }, RING_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [call.status, cleanup, applyAuthoritativeAction]);

  useEffect(() => {
    if (call.status !== 'connecting') return;
    const id = setTimeout(() => {
      if (statusRef.current !== 'connecting' || !routeRef.current) return;
      logCallLifecycle('CALL_FAILED', routeRef.current.callId, {
        peer: routeRef.current.peerUserId,
        role: isCallerRef.current ? 'caller' : 'callee',
        reason: 'connect-timeout',
        answerApplied: isCallerRef.current ? answerAppliedRef.current : null,
        remoteCandidates: seenCandidateKeysRef.current.size,
      });
      void applyAuthoritativeAction('media-disconnected').then(() => {
        cleanup('Call failed', true, 'failed');
      });
    }, CONNECT_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [call.status, cleanup, applyAuthoritativeAction]);

  // While media connects, keep pulling what the socket relay may have lost.
  useEffect(() => {
    // P2P only: a direct SFU call has no SDP or ICE to recover.
    if (call.status !== 'connecting' || directSfuRef.current) return;
    let id: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (statusRef.current !== 'connecting') return;
      void recoverSignaling('connecting');
      id = setTimeout(tick, SIGNALING_RECOVERY_EVERY_MS);
    };
    id = setTimeout(tick, SIGNALING_RECOVERY_FIRST_MS);
    return () => clearTimeout(id);
  }, [call.status, recoverSignaling]);

  // Persist lifecycle state when this renderer goes away. Terminal direct
  // frames are intentionally rejected by the API; call-state is the durable
  // cross-client terminal source, so unload uses authenticated keepalive fetch.
  useEffect(() => {
    const notifyPeer = () => {
      const s = statusRef.current;
      if (s === 'idle' || s === 'ended') return;
      const route = routeRef.current;
      const authoritative = snapshotRef.current;
      if (
        route
        && authoritative?.callId === route.callId
        && (authoritative.status === 'ringing' || authoritative.status === 'active')
        && unloadActionRef.current?.callId !== route.callId
      ) {
        const eventId = createCallEventId('call-unload');
        unloadActionRef.current = { callId: route.callId, eventId };
        actOnCallKeepalive(route.callId, {
          action: terminalActionFor(isCallerRef.current, authoritative.status === 'ringing', directSfuRef.current),
          eventId,
          expectedVersion: authoritative.version,
        });
      }
      sfuMediaRef.current?.close();
      // Kill local audio synchronously — don't wait for React's unmount effect,
      // or the ring can keep firing for up to one interval while the tab closes.
      stopAllCallSounds();
      // Withdraw the OS call notification too: after the unload nothing else
      // remembers it (fire-and-forget IPC is fine during unload).
      if (notifiedCallRef.current) void closeCallNotification(notifiedCallRef.current.callId);
      // Persist THIS side's row even as the tab closes (keepalive fetch
      // survives unload; the normal cleanup POST would be aborted).
      const meta = callMetaRef.current;
      if (meta && !loggedCallIdsRef.current.includes(meta.callId)) {
        loggedCallIdsRef.current.push(meta.callId);
        const outcome: CallOutcome = meta.direction === 'outgoing'
          ? (meta.answeredAt ? 'completed' : 'cancelled')
          : (meta.answeredAt ? 'completed' : 'missed');
        const endedAt = Date.now();
        logCallKeepalive({
          call_id: meta.callId, peer_user_id: meta.peerUserId, conversation_id: meta.conversationId,
          direction: meta.direction, call_type: 'voice', outcome,
          started_at: new Date(meta.ringStartedAt).toISOString(),
          answered_at: meta.answeredAt ? new Date(meta.answeredAt).toISOString() : null,
          ended_at: new Date(endedAt).toISOString(),
          duration_s: meta.answeredAt ? Math.max(0, Math.round((endedAt - meta.answeredAt) / 1000)) : 0,
        });
        if (meta.direction === 'incoming' && outcome === 'missed') bumpMissed();
      }
    };
    window.addEventListener('beforeunload', notifyPeer);
    window.addEventListener('pagehide', notifyPeer);
    return () => {
      window.removeEventListener('beforeunload', notifyPeer);
      window.removeEventListener('pagehide', notifyPeer);
    };
  }, []);

  // Cross-tab call resolution. The backend rings EVERY tab the user has open;
  // a reject/accept in one tab is invisible to the others, so they'd ring on
  // until their own timeout. We listen for a sibling's 'call-resolved' and, if
  // THIS tab is merely ringing (incoming) for that same call, dismiss it too.
  // Guarded to 'incoming' so the tab actually handling the call (outgoing /
  // connecting / connected) is never torn down by a sibling — also keeps the
  // same-account two-tab "self-call" test from collapsing the caller tab.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('messenger-call');
    callChannelRef.current = ch;
    ch.onmessage = (e: MessageEvent) => {
      const m = e.data;
      if (
        m?.type === 'call-resolved' && m.callId &&
        routeRef.current?.callId === m.callId &&
        statusRef.current === 'incoming'
      ) {
        cleanup(null, false, 'none'); // false: don't re-broadcast (avoid a ping-pong)
      }
    };
    return () => { ch.close(); callChannelRef.current = null; };
  }, [cleanup]);

  const openVerifiedInvite = useCallback((frame: any, authorized: CallSnapshot) => {
    if (
      !isAuthorizedRingingInvite(frame, authorized)
      || routeRef.current !== null
      || statusRef.current !== 'idle'
      || isOtherCallActive('oneToOne')
    ) {
      logCallDiagnostic(
        `invite call=${frame.call_id} not opened: ${authorized.status} v${authorized.version}, `
        + `ui=${statusRef.current}, route=${routeRef.current ? 'busy' : 'none'}`,
      );
      return;
    }

    storeSnapshot(authorized);
    logCallDiagnostic(`ringing call=${frame.call_id} at v${authorized.version} (known v${snapshotRef.current?.version})`);
    routeRef.current = {
      callId: frame.call_id, conversationId: frame.conversation_id, peerUserId: frame.from_user_id,
    };
    unloadActionRef.current = null;
    callMetaRef.current = {
      callId: frame.call_id, peerUserId: frame.from_user_id, conversationId: frame.conversation_id,
      direction: 'incoming', ringStartedAt: Date.now(), answeredAt: null,
    };
    isCallerRef.current = false;
    answerAppliedRef.current = false;
    remoteDescriptionAppliedRef.current = false;
    seenCandidateKeysRef.current = new Set();
    postedCandidateKeysRef.current = new Set();
    localCandidatesRef.current = [];
    pendingOffer.current = frame.sdp;
    setMinimized(false);
    logCallLifecycle('CALL_RINGING', frame.call_id, {
      peer: frame.from_user_id, role: 'callee', version: authorized.version, state: 'ringing',
    });
    setCall({
      ...IDLE, status: 'incoming', callId: frame.call_id, conversationId: frame.conversation_id,
      peerUserId: frame.from_user_id, peerName: frame.from_name ?? frame.from_user_id,
      peerAvatarUrl: frame.from_avatar_url ?? null,
    });
    // The ring is on screen: the caller's "Calling…" becomes "Ringing…".
    void reportCallAlerted(frame.call_id, authorized.version);
    // If the invite carried no caller photo, resolve it from the conversation
    // and patch the already-authorized incoming surface.
    if (!frame.from_avatar_url && frame.conversation_id) {
      const callId = frame.call_id;
      const peerId = frame.from_user_id;
      void getConversation(frame.conversation_id).then((conv: any) => {
        const peer = conv?.members?.find((m: any) => m.id === peerId || m.user_id === peerId);
        const url = peer?.avatar_url || (conv?.type !== 'dm' ? conv?.avatar_url : null) || null;
        if (url) setCall((current) => current.callId === callId ? { ...current, peerAvatarUrl: url } : current);
      }).catch(() => { /* best-effort */ });
    }
  }, [storeSnapshot]);

  const declineInviteWithoutOpening = useCallback((frame: any, authorized: CallSnapshot) => {
    if (!isAuthorizedRingingInvite(frame, authorized)) return;
    const eventId = createCallEventId('call-decline');
    void actOnCall(frame.call_id, {
      action: 'decline', eventId, expectedVersion: authorized.version,
    }).catch(() => { /* stale or unauthorized invites never create UI */ });
  }, []);

  /**
   * Ring for a direct SFU call. It has no call-invite (there is no SDP), so
   * the authoritative ringing snapshot that names this user as callee is the
   * ring itself, and it gets the same 1:1 incoming screen as a P2P call.
   */
  const openDirectSfuIncoming = useCallback(async (authorized: CallSnapshot) => {
    const callId = authorized.callId;
    if (
      !selfId
      || !isDirectSfuSnapshot(authorized)
      || authorized.status !== 'ringing'
      || authorized.initiatorUserId === selfId
      || !authorized.participantUserIds.includes(selfId)
      || declinedCallIdsRef.current.includes(callId)
      || openingDirectRef.current === callId
      || routeRef.current?.callId === callId
    ) return;
    const declineUnopened = (why: string) => {
      logCallDiagnostic(`direct ring call=${callId} declined: ${why}`);
      void actOnCall(callId, {
        action: 'decline', eventId: createCallEventId('call-decline'), expectedVersion: authorized.version,
      }).catch(() => { /* stale ring */ });
    };
    if (isDndQuiet) { declineUnopened('quiet mode'); return; }
    if (routeRef.current || statusRef.current !== 'idle' || isOtherCallActive('oneToOne')) {
      declineUnopened(`busy (ui=${statusRef.current})`);
      return;
    }

    openingDirectRef.current = callId;
    const peerUserId = authorized.initiatorUserId;
    let peerName: string | null = null;
    let peerAvatarUrl: string | null = null;
    let lookupTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      const conv: any = await Promise.race([
        getConversation(authorized.conversationId),
        new Promise((resolve) => { lookupTimer = setTimeout(() => resolve(null), DIRECT_PEER_LOOKUP_MS); }),
      ]);
      const member = conv?.members?.find((m: any) => m.id === peerUserId || m.user_id === peerUserId);
      peerName = member?.name ?? member?.display_name ?? null;
      peerAvatarUrl = member?.avatar_url ?? null;
    } catch { /* ring without the name rather than not at all */ } finally {
      if (lookupTimer) clearTimeout(lookupTimer);
      if (openingDirectRef.current === callId) openingDirectRef.current = null;
    }

    // The ring may have been answered elsewhere, cancelled, or another call
    // may have opened while the name loaded.
    const latest = snapshotRef.current?.callId === callId ? snapshotRef.current : authorized;
    if (
      latest.status !== 'ringing'
      || routeRef.current
      || statusRef.current !== 'idle'
      || isOtherCallActive('oneToOne')
      || declinedCallIdsRef.current.includes(callId)
    ) return;
    storeSnapshot(latest);
    routeRef.current = { callId, conversationId: latest.conversationId, peerUserId };
    unloadActionRef.current = null;
    callMetaRef.current = {
      callId, peerUserId, conversationId: latest.conversationId,
      direction: 'incoming', ringStartedAt: Date.now(), answeredAt: null,
    };
    isCallerRef.current = false;
    directSfuRef.current = true;
    answerAppliedRef.current = false;
    remoteDescriptionAppliedRef.current = false;
    seenCandidateKeysRef.current = new Set();
    postedCandidateKeysRef.current = new Set();
    localCandidatesRef.current = [];
    pendingOffer.current = null;
    setMinimized(false);
    logCallLifecycle('CALL_RINGING', callId, {
      peer: peerUserId, role: 'callee', version: latest.version, state: 'ringing', via: 'sfu',
    });
    setCall({
      ...IDLE, status: 'incoming', callId, conversationId: latest.conversationId,
      peerUserId, peerName: peerName ?? 'Voice call', peerAvatarUrl,
    });
    void reportCallAlerted(callId, latest.version);
  }, [isDndQuiet, selfId, storeSnapshot]);
  openDirectIncomingRef.current = openDirectSfuIncoming;

  const recoverCalls = useCallback((): Promise<void> => {
    if (recoveryInFlightRef.current) return recoveryInFlightRef.current;

    const recovery = (async () => {
      try {
        const { calls } = await syncCalls();
        const route = routeRef.current;
        if (route) {
          const routed = calls.find((item) => item.callId === route.callId);
          logCallDiagnostic(
            `recover call=${route.callId} ui=${statusRef.current}: server ${routed ? `v${routed.version} ${routed.status}` : 'has no such call'}`,
          );
          if (routed) reconcileSnapshot(routed);
          // Frames relayed while the socket was down are gone; the server kept them.
          if (routed?.status === 'active' && statusRef.current !== 'connected' && !directSfuRef.current) {
            void recoverSignaling('socket-reconnect');
          }
          return;
        }
        if (statusRef.current !== 'idle') return;

        // Android wakes the app with an opaque pointer only. Re-authorize the
        // call and fetch the short-lived offer through the authenticated API;
        // native code never supplies SDP or ICE material.
        const nativePointer = await getPendingNativeCall();
        if (nativePointer) {
          const authorized = calls.find(item => item.callId === nativePointer.callId);
          if (authorized?.status === 'ringing' && authorized.mediaTopology === 'p2p' && !authorized.isGroup) {
            try {
              const offer = await getP2pOffer(authorized.callId);
              if (offer.call_id === authorized.callId && offer.media === 'audio') {
                nativeCallIdRef.current = nativePointer.callId;
                openVerifiedInvite(offer, authorized);
                if (routeRef.current) return;
              }
            } catch {
              // The offer may have expired; the next push/socket wake retries.
            }
          } else {
            await clearNativeIncomingCall(nativePointer.callId);
          }
        }

        // Offer recovery authorizes the current viewer as the callee and gives
        // us the caller identity/SDP needed to rebuild the existing answer UI.
        const ringingP2p = calls.filter((item) => (
          item.status === 'ringing' && item.mediaTopology === 'p2p' && !item.isGroup
        ));
        for (const authorized of ringingP2p) {
          try {
            const offer = await getP2pOffer(authorized.callId);
            if (routeRef.current || statusRef.current !== 'idle') return;
            if (isDndQuiet) declineInviteWithoutOpening(offer, authorized);
            else openVerifiedInvite(offer, authorized);
            if (routeRef.current) return;
          } catch {
            // The initiator cannot recover its own offer; try another ringing
            // P2P session that may be an authorized incoming call.
          }
        }

        const ringingDirect = calls.find((item) => (
          item.status === 'ringing'
          && isDirectSfuSnapshot(item)
          && !!selfId
          && item.initiatorUserId !== selfId
          && item.participantUserIds.includes(selfId)
        ));
        if (ringingDirect) {
          await openDirectSfuIncoming(ringingDirect);
          if (routeRef.current) return;
        }

        const latestP2p = calls.find((item) => item.mediaTopology === 'p2p' && !item.isGroup);
        if (latestP2p) reconcileSnapshot(latestP2p);
      } catch (error) {
        // The next socket open/state frame retries recovery.
        logCallDiagnostic(`recover failed: ${describeCallError(error)}`);
      } finally {
        recoveryInFlightRef.current = null;
      }
    })();
    recoveryInFlightRef.current = recovery;
    return recovery;
  }, [
    declineInviteWithoutOpening, isDndQuiet, openDirectSfuIncoming, openVerifiedInvite, reconcileSnapshot,
    recoverSignaling, selfId,
  ]);

  useEffect(() => {
    void recoverCalls();
  }, [recoverCalls]);

  const resolveLegacyTerminalFrame = useCallback((type: string) => {
    const route = routeRef.current;
    if (!route) return;
    const eventId = createCallEventId('call-legacy-terminal');

    const isTerminal = (candidate: CallSnapshot) => isTerminalSnapshot(candidate.status);
    const actionFor = (candidate: CallSnapshot): CallLifecycleAction | null => {
      if (candidate.status !== 'ringing' && candidate.status !== 'active') return null;
      return terminalActionFor(isCallerRef.current, candidate.status === 'ringing');
    };
    const finish = (terminal: CallSnapshot) => {
      if (terminal.callId !== route.callId || !isTerminal(terminal)) return;
      reconcileSnapshot(terminal);
      // reconcileSnapshot normally performs cleanup. If this action's own
      // event is still pending, it deliberately defers cleanup to this owner.
      if (routeRef.current?.callId !== route.callId) return;
      const meta = callMetaRef.current;
      if (type === 'call-busy') cleanup('User busy', true, 'busy');
      else if (type === 'call-reject') cleanup('Call declined', true, 'declined');
      else if (type === 'call-cancel') {
        cleanup('Call cancelled', true, meta?.direction === 'incoming' ? 'missed' : 'cancelled');
      } else {
        cleanup('Call ended', true, meta?.answeredAt ? 'completed' : 'failed');
      }
    };

    void (async () => {
      try {
        let current = await getCallSnapshot(route.callId);
        if (routeRef.current?.callId !== route.callId || current.callId !== route.callId) return;
        if (isTerminal(current)) {
          finish(current);
          return;
        }

        let action = actionFor(current);
        if (!action) return;
        storeSnapshot(current);
        pendingActionRef.current = { callId: route.callId, eventId };
        try {
          const terminal = await actOnCall(route.callId, {
            action, eventId, expectedVersion: current.version,
          });
          finish(terminal);
          return;
        } catch (error: any) {
          if (error?.response?.status !== 409) return;
        }

        // State may have crossed ringing→active between the read and action.
        // Recompute the transition, but retain the idempotency event id.
        current = await getCallSnapshot(route.callId);
        if (routeRef.current?.callId !== route.callId || current.callId !== route.callId) return;
        if (isTerminal(current)) {
          finish(current);
          return;
        }
        action = actionFor(current);
        if (!action) return;
        storeSnapshot(current);
        const terminal = await actOnCall(route.callId, {
          action, eventId, expectedVersion: current.version,
        });
        finish(terminal);
      } catch {
        // A later authoritative call-state frame remains the cleanup source.
      } finally {
        if (pendingActionRef.current?.eventId === eventId) pendingActionRef.current = null;
      }
    })();
  }, [cleanup, reconcileSnapshot, storeSnapshot]);

  const handleSignal = useCallback((frame: any) => {
    const t = frame?.type;
    if (t === 'call-invite') {
      if (
        typeof frame.call_id !== 'string'
        || typeof frame.conversation_id !== 'string'
        || typeof frame.from_user_id !== 'string'
        || !frame.sdp
        || frame.sdp.type !== 'offer'
      ) return;
      // Local failsafe for a frame already in flight when DND was enabled (or
      // from an older server): never create a ring, native popup, or call UI.
      if (isDndQuiet) {
        logCallDiagnostic(`invite call=${frame.call_id} declined: quiet mode`);
        const known = snapshotRef.current?.callId === frame.call_id ? snapshotRef.current : null;
        if (known) declineInviteWithoutOpening(frame, known);
        else void getCallSnapshot(frame.call_id)
          .then((authorized) => declineInviteWithoutOpening(frame, authorized))
          .catch(() => { /* unauthorized or stale */ });
        return;
      }
      // Already resolved this call id (declined/ended, possibly in another tab)
      // → ignore a duplicate/re-published invite so it can't re-ring.
      if (frame.call_id && declinedCallIdsRef.current.includes(frame.call_id)) {
        logCallDiagnostic(`invite call=${frame.call_id} ignored: already resolved here`);
        return;
      }
      const verifyAndOpen = (authorized: CallSnapshot) => {
        if (routeRef.current || statusRef.current !== 'idle' || isOtherCallActive('oneToOne')) {
          logCallDiagnostic(`invite call=${frame.call_id} declined: busy (ui=${statusRef.current})`);
          declineInviteWithoutOpening(frame, authorized);
          return;
        }
        openVerifiedInvite(frame, authorized);
      };
      const known = snapshotRef.current?.callId === frame.call_id ? snapshotRef.current : null;
      logCallDiagnostic(`invite call=${frame.call_id} received (known ${known ? `v${known.version} ${known.status}` : 'none: fetching'})`);
      if (known) verifyAndOpen(known);
      else void getCallSnapshot(frame.call_id).then(verifyAndOpen).catch((error) => {
        // Unauthorized or stale.
        logCallDiagnostic(`invite call=${frame.call_id} unverified: ${describeCallError(error)}`);
      });
      return;
    }
    // Ignore frames that don't belong to the current call.
    if (!routeRef.current || frame.call_id !== routeRef.current.callId) return;

    if (t === 'call-answer') {
      if (!isCallerRef.current) {
        // Echo of THIS user answering in another session (the backend fans
        // the answer back to the answerer's other sockets): if we're a
        // still-ringing callee tab, dismiss silently — answered elsewhere,
        // not missed. The session that actually answered is past 'incoming'
        // and ignores its own echo here.
        if (statusRef.current === 'incoming') cleanup(null, true, 'none');
        return;
      }
      // Only the caller applies an answer, and only once (see refs above).
      if (answerAppliedRef.current) return;
      const applyAnswer = (authorized: CallSnapshot) => {
        if (authorized.callId !== frame.call_id || authorized.status !== 'active' || answerAppliedRef.current) return;
        storeSnapshot(authorized);
        applyRemoteAnswer(frame.call_id, frame.sdp, 'socket');
      };
      const known = snapshotRef.current?.callId === frame.call_id ? snapshotRef.current : null;
      if (known?.status === 'active') applyAnswer(known);
      else void getCallSnapshot(frame.call_id).then(applyAnswer).catch(() => { /* unauthorized/stale answer */ });
    } else if (t === 'ice-candidate') {
      addRemoteCandidate(frame.candidate);
    } else if (t === 'call-reject') {
      resolveLegacyTerminalFrame(t);
    } else if (t === 'call-busy') {
      resolveLegacyTerminalFrame(t);
    } else if (t === 'call-cancel') {
      resolveLegacyTerminalFrame(t);
    } else if (t === 'call-end') {
      resolveLegacyTerminalFrame(t);
    }
  }, [
    addRemoteCandidate, applyRemoteAnswer, cleanup, isDndQuiet, openVerifiedInvite, declineInviteWithoutOpening,
    resolveLegacyTerminalFrame, storeSnapshot,
  ]);

  // Recording is its own channel (REST + `call_recording` frames), never the
  // call snapshot; any failure in it leaves the call untouched.
  const recording = useCallRecording(
    call.status === 'idle' || call.status === 'ended' ? null : call.callId,
    call.status === 'connected',
  );
  const recordingCallId = recording?.call_id ?? null;
  const startRecording = useCallback(
    () => (recordingCallId ? startRecordingForCall(recordingCallId) : Promise.resolve()),
    [recordingCallId],
  );

  const apiValue: CallApi = {
    call, snapshot, minimized, startCall, accept, reject, hangup, toggleMute, toggleVideo, toggleMinimize, minimize,
    handleSignal, handleCallState, recoverCalls, bindSend, bindEscalate,
  };

  return (
    <Ctx.Provider value={apiValue}>
      {children}
      {call.status !== 'idle' && (
        <CallScreen
          status={call.status}
          peerRinging={call.peerRinging}
          peerName={call.peerName}
          peerAvatarUrl={call.peerAvatarUrl}
          muted={call.muted}
          startedAt={call.startedAt}
          endReason={call.endReason}
          minimized={minimized}
          remoteSpeaking={remoteSpeaking}
          recordingStatus={recording?.status ?? null}
          recordingStartMode={recording?.start_mode ?? null}
          canStartRecording={!!recording?.can_start}
          onStartRecording={startRecording}
          onAccept={accept}
          onReject={reject}
          onToggleMute={toggleMute}
          // P2P 1:1 calls are audio-only: no camera button there.
          onToggleVideo={videoReady ? toggleVideo : undefined}
          cameraOn={!!localVideoTrack}
          cameraBusy={cameraBusy}
          localVideoTrack={localVideoTrack}
          remoteVideoTrack={remoteVideoTrack}
          // Direct SFU calls only, in a browser that can capture the screen.
          onToggleScreenShare={videoReady && canShareScreen() ? toggleScreenShare : undefined}
          screenSharing={screenSharing}
          screenShareBusy={screenShareBusy}
          remoteScreenTrack={remoteScreenTrack}
          onHangup={hangup}
          onToggleMinimize={toggleMinimize}
          onMinimize={minimize}
          onAddParticipant={() => setAddingOpen(true)}
        />
      )}
      {addingOpen && call.peerUserId && (
        <StartCallPicker
          multi
          title="Add to call"
          excludeUserIds={[call.peerUserId]}
          onClose={() => setAddingOpen(false)}
          onPickMany={(users) => {
            setAddingOpen(false);
            escalateRef.current?.({
              callId: call.callId, peerUserId: call.peerUserId!, peerName: call.peerName, newUserIds: users.map(u => u.user_id),
            });
          }}
        />
      )}
      {/* Remote audio sink — hidden, autoplay. */}
      <audio ref={remoteAudioRef} autoPlay />
    </Ctx.Provider>
  );
}

export function useCall(): CallApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCall must be used within a CallProvider');
  return ctx;
}
